import { ReactNode } from 'react'
import { useMatches, Link as NavLink, UIMatch } from 'react-router-dom'
import { Box } from '@mui/material'
import HomeIcon from '@mui/icons-material/Home'
import { useSubCrumb } from './SubCrumb.context'

/**
 * A route opts into the trail with `handle: { crumb }`. The crumb returns a
 * string, or a node for a label that has to be fetched (a project or scenario
 * name — see RouteCrumbs.tsx).
 */
export type CrumbHandle = {
  crumb: (match: UIMatch) => ReactNode
}

const LINK_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  fontFamily: 'var(--font-sans)',
  fontSize: 12.5,
  fontWeight: 700,
  color: 'var(--ink-700)',
  textDecoration: 'none',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  p: 0,
  maxWidth: 320,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  '&:hover': { textDecoration: 'underline' },
} as const

const CURRENT_SX = {
  fontFamily: 'var(--font-sans)',
  fontSize: 12.5,
  color: 'var(--ink-500)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
} as const

const titleOf = (label: ReactNode) =>
  typeof label === 'string' ? label : undefined

const Separator = () => (
  <Box component="span" aria-hidden="true" sx={{ color: 'var(--ink-300)' }}>
    /
  </Box>
)

/**
 * The crumb trail, shared with the Planner Portal and matching the Scheduler's:
 * slash separators, the links carrying the weight and the page you are on
 * muted. Replaces the MUI `Breadcrumbs` that only the Edit Settings view drew.
 *
 * A page may append one more crumb through `usePublishSubCrumb` for a view that
 * is component state rather than a route. When it does, the last route crumb
 * becomes a button that clears it — that is the way back.
 */
export const Breadcrumbs = () => {
  const matches = useMatches()
  const { subCrumb } = useSubCrumb()

  const crumbs = matches
    .filter((match) =>
      Boolean((match.handle as CrumbHandle | undefined)?.crumb)
    )
    .map((match) => ({
      label: (match.handle as CrumbHandle).crumb(match),
      pathname: match.pathname,
    }))

  if (!crumbs.length) {
    return null
  }

  return (
    <Box
      component="nav"
      aria-label="Breadcrumb"
      sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}
    >
      <Box
        component="ol"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          listStyle: 'none',
          m: 0,
          p: 0,
          minWidth: 0,
        }}
      >
        {crumbs.map((crumb, index) => {
          const isFirst = index === 0
          const isLastRoute = index === crumbs.length - 1
          // The trail's true end is the sub-crumb when a page has published one.
          const isCurrent = isLastRoute && !subCrumb

          return (
            <Box
              component="li"
              key={`${crumb.pathname}-${index}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                minWidth: 0,
              }}
            >
              {index > 0 && <Separator />}

              {isCurrent ? (
                <Box component="span" aria-current="page" sx={CURRENT_SX}>
                  {crumb.label}
                </Box>
              ) : isLastRoute && subCrumb ? (
                // Same route, different view: clear the view rather than navigate.
                <Box
                  component="button"
                  type="button"
                  onClick={subCrumb.onClear}
                  title={titleOf(crumb.label)}
                  sx={LINK_SX}
                >
                  {crumb.label}
                </Box>
              ) : (
                <Box
                  component={NavLink}
                  to={crumb.pathname}
                  title={titleOf(crumb.label)}
                  sx={{
                    ...LINK_SX,
                    ...(isFirst && {
                      fontWeight: 600,
                      color: 'var(--brand-teal)',
                    }),
                  }}
                >
                  {isFirst && <HomeIcon sx={{ fontSize: 14, flexShrink: 0 }} />}
                  {crumb.label}
                </Box>
              )}
            </Box>
          )
        })}

        {subCrumb && (
          <Box
            component="li"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: 0,
            }}
          >
            <Separator />
            <Box component="span" aria-current="page" sx={CURRENT_SX}>
              {subCrumb.label}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}
